// Line-map helpers for merged `.luis` sources with inlined imports.
// Each helper keeps merged output lines aligned with original file positions.
import type { LuisSourceLineOrigin, ResolvedImportSource } from './line-map-types'

// Build one empty resolved-source payload for incremental appends.
export function createResolvedImportSource(): ResolvedImportSource {
  // Return a new empty source payload.
  return { source: '', lineMap: [], outputLine: 1 }
}

// Append one raw character and register its source origin for the active line.
export function appendRawChar(result: ResolvedImportSource, char: string, sourceId: string, line: number): void {
  ensureLineOrigin(result, sourceId, line)
  result.source += char
  // Advance cached output line after newline writes.
  if (char === '\n') {
    result.outputLine += 1
  } else {
    // Keep cached output line on the current row.
  }
}

// Append one nested resolved source while preserving each line origin.
export function appendResolvedImportSource(target: ResolvedImportSource, chunk: ResolvedImportSource): void {
  let chunkLine = 1
  // Scan one nested chunk and mirror its line origins into the parent output.
  for (let index = 0; index < chunk.source.length; index += 1) {
    const char = chunk.source[index]
    const origin = resolveChunkOrigin(chunk, chunkLine)
    appendRawChar(target, char, origin.sourceId, origin.line)
    if (char === '\n') {
      chunkLine += 1
    } else {
      // Continue nested chunk scan on the same source line.
    }
  }
}

// Append one wrapped import body using the same newline policy as compile-time imports.
export function appendWrappedImportSource(
  target: ResolvedImportSource,
  chunk: ResolvedImportSource,
  sourceId: string,
  line: number,
): void {
  // Skip wrapper output when nested import source is empty.
  if (!chunk.source) {
    return
  } else {
    // Continue wrapped import output assembly.
  }

  if (!chunk.source.startsWith('\n')) {
    appendRawChar(target, '\n', sourceId, line)
  } else {
    // Keep existing leading newline from nested import source.
  }
  appendResolvedImportSource(target, chunk)
  if (!chunk.source.endsWith('\n')) {
    appendRawChar(target, '\n', sourceId, line)
  } else {
    // Keep existing trailing newline from nested import source.
  }
}

// Build one flat line map for sources that were not import-expanded.
export function createFlatResolvedImportSource(source: string, sourceId: string): ResolvedImportSource {
  const result = createResolvedImportSource()
  let line = 1
  // Scan source once and assign the current origin to each output line.
  for (let index = 0; index < source.length; index += 1) {
    appendRawChar(result, source[index], sourceId, line)
    if (source[index] === '\n') {
      line += 1
    } else {
      // Continue flat-source scan on the same source line.
    }
  }

  // Return the flat source payload with per-line origins.
  return result
}

// Resolve one chunk line to its nearest known source origin.
function resolveChunkOrigin(chunk: ResolvedImportSource, line: number): LuisSourceLineOrigin {
  if (chunk.lineMap[line - 1]) {
    // Return the exact line origin when it exists.
    return chunk.lineMap[line - 1]
  } else if (chunk.lineMap.length > 0) {
    // Return the last known origin for trailing empty lines.
    return chunk.lineMap[chunk.lineMap.length - 1]
  } else {
    // Return an inline fallback when chunk has no explicit line map.
    return { sourceId: '<inline>', line: 1 }
  }
}

// Register the current output line origin only once per merged line.
function ensureLineOrigin(result: ResolvedImportSource, sourceId: string, line: number): void {
  const outputLine = result.outputLine
  if (!result.lineMap[outputLine - 1]) {
    result.lineMap[outputLine - 1] = { sourceId, line }
  } else {
    // Keep the existing origin for already-mapped output lines.
  }
}
